import {
    AssetManager,
    AtlasAttachmentLoader,
    SkeletonBinary,
    SkeletonJson
} from '@esotericsoftware/spine-webgl';

class SpineAssetService {
    constructor() {
        // 缓存原始文本/二进制数据 (atlas text + skeleton binary/json)
        this.rawDataCache = new Map();
        this.loadingPromises = new Map();
    }

    /**
     * 获取 SkeletonData 并结合 WebGLContext 组装成渲染对象
     */
    async getSkeletonData(assetKey, modelName, assetsPath, isJson, webglCtx) {
        // 1. 获取或下载原始数据
        const rawData = await this.getRawData(assetKey, modelName, assetsPath, isJson);

        // 2. 为当前 WebGL Context 实例化 AssetManager
        const assetManager = new AssetManager(webglCtx);

        // 3. 解析该上下文下的图集和骨骼数据
        return this.parseSkeletonData(rawData, assetManager, modelName, assetsPath, isJson);
    }

    /**
     * 全局缓存的 HTTP 数据请求（只请求一次）
     */
    async getRawData(assetKey, modelName, assetsPath, isJson) {
        if (this.rawDataCache.has(assetKey)) {
            return this.rawDataCache.get(assetKey);
        }

        if (this.loadingPromises.has(assetKey)) {
            return this.loadingPromises.get(assetKey);
        }

        const loadPromise = this.loadRawData(modelName, assetsPath, isJson);
        this.loadingPromises.set(assetKey, loadPromise);

        try {
            // ✅ 正确：直接 await 这个 Promise 对象
            const data = await loadPromise;
            this.rawDataCache.set(assetKey, data);
            return data;
        } finally {
            this.loadingPromises.delete(assetKey);
        }
    }

    /**
     * 通过 fetch 加载 HTTP 原始资源
     */
    async loadRawData(modelName, assetsPath, isJson) {
        const normalizedPath = assetsPath.replace(/\/$/, '');
        const atlasUrl = `${normalizedPath}/${modelName}.atlas`;
        const skeletonUrl = isJson
            ? `${normalizedPath}/${modelName}.json`
            : `${normalizedPath}/${modelName}.skel`;

        const [atlasResponse, skeletonResponse] = await Promise.all([
            fetch(atlasUrl),
            fetch(skeletonUrl)
        ]);

        if (!atlasResponse.ok || !skeletonResponse.ok) {
            throw new Error(`Failed to load assets: ${atlasUrl}, ${skeletonUrl}`);
        }

        const atlasText = await atlasResponse.text();
        const skeletonData = isJson
            ? await skeletonResponse.text()
            : await skeletonResponse.arrayBuffer();

        return {
            atlasText,
            skeletonData
        };
    }

    /**
     * 将缓存解析为对应 WebGL 上下文的 Spine 对象
     */
    async parseSkeletonData(rawData, assetManager, modelName, assetsPath, isJson) {
        const normalizedPath = assetsPath.replace(/\/$/, '');
        const atlasPath = `${normalizedPath}/${modelName}.atlas`;

        if (!assetManager.get(atlasPath)) {
            // 通过 base64 dataURI 注入已缓存的 atlas 文本，避免二次网络请求
            const base64Data = btoa(
                unescape(encodeURIComponent(rawData.atlasText))
            );
            assetManager.setRawDataURI(atlasPath, `data:text/plain;base64,${base64Data}`);
            assetManager.loadText(atlasPath);
        }

        assetManager.loadTextureAtlas(atlasPath);

        // 轮询等待纹理贴图加载完毕
        await new Promise((resolve, reject) => {
            const checkLoaded = () => {
                if (assetManager.isLoadingComplete()) {
                    if (assetManager.hasErrors()) {
                        reject(new Error(`Failed to load textures: ${assetManager.getErrors()}`));
                    } else {
                        resolve();
                    }
                } else {
                    window.requestAnimationFrame(checkLoaded);
                }
            };
            checkLoaded();
        });

        const atlas = assetManager.require(atlasPath);
        const atlasLoader = new AtlasAttachmentLoader(atlas);

        const skeletonParser = isJson
            ? new SkeletonJson(atlasLoader)
            : new SkeletonBinary(atlasLoader);

        skeletonParser.scale = 1;

        const parsedRaw = typeof rawData.skeletonData === 'string'
            ? JSON.parse(rawData.skeletonData)
            : new Uint8Array(rawData.skeletonData);

        const skeletonData = skeletonParser.readSkeletonData(parsedRaw);

        return {
            skeletonData,
            atlas
        };
    }

    clearCache(assetKey) {
        this.rawDataCache.delete(assetKey);
    }

    clearAllCache() {
        this.rawDataCache.clear();
        this.loadingPromises.clear();
    }

    isCached(assetKey) {
        return this.rawDataCache.has(assetKey);
    }

    isLoading(assetKey) {
        return this.loadingPromises.has(assetKey);
    }
}

// 导出单例实例
export const spineAssetService = new SpineAssetService();
export default spineAssetService;